var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useCallback, useEffect, useState, useRef, } from 'react';
import shortid from 'shortid';
import { isUndefined } from 'lodash-es';
import styled, { css } from '../../../styled';
import { Title, Message, MessageWrapper } from '../../input';
import DateInput from './Input/DateInput';
import Calendar from './Calendar';
import dayjs, { isValidDateStr } from './day';
import { getDefaultPlaceholder, getDefaultClearTooltip, getDefaultInputTooltip, getCalendarBottomPosition, } from '.';
export const Container = styled.div `
  display: ${({ block }) => (block ? 'flex' : 'inline-flex')};
  position: relative;
  flex-direction: column;
`;
const positionBottom = css `
  top: ${({ top }) => top}rem;
`;
const positionTop = css `
  ${({ bottom }) => !isUndefined(bottom) && `bottom: ${bottom}rem;`}
`;
export const CalendarContainer = styled.div `
  display: ${({ open }) => (open ? 'block' : 'none')};
  position: absolute;
  ${({ calendarPosition }) => calendarPosition === 'TOP' ? positionTop : positionBottom}
  padding: 0 1rem 1rem 1rem;
  border-radius: 8px;
  box-shadow: 0 0 1rem 0 ${({ theme }) => theme.colors.INK_A100},
    0 0.0625rem 0.1875rem 0 ${({ theme }) => theme.colors.INK_A200};
  background-color: ${({ theme }) => theme.colors.SNOW_100};
  user-select: none;
  z-index: 1;
  cursor: default;
  ${({ calendarAlign = 'left' }) => calendarAlign === 'right'
    ? css `
          right: 0;
        `
    : css `
          left: 0;
        `}
`;
const DatePicker = (_a) => {
    var { lang = 'en', format = 'YYYY-MM-DD', inputRef = React.createRef(), inputWidth = 8, status = 'valid', title, renderMessages = [], block = false, disabled, value, defaultValue, placeholder, calendarAlign, onDateChange, disabledDate, calendarPosition = 'BOTTOM' } = _a, restProps = __rest(_a, ["lang", "format", "inputRef", "inputWidth", "status", "title", "renderMessages", "block", "disabled", "value", "defaultValue", "placeholder", "calendarAlign", "onDateChange", "disabledDate", "calendarPosition"]);
    const containerRef = useRef(null);
    const titleRef = useRef(null);
    const [open, setOpen] = useState(false); // indicate that calendar is opened or not
    const [inputValue, setInputValue] = useState(defaultValue ? dayjs(lang, defaultValue).format(format) : '');
    const [selectedDate, setSelectedDate] = useState(defaultValue ? dayjs(lang, defaultValue) : undefined);
    const date = value ? dayjs(lang, value) : selectedDate;
    const handleDateChange = useCallback((newDate) => {
        setSelectedDate(newDate);
        setInputValue(newDate.format(format));
        setOpen(false);
        if (onDateChange)
            onDateChange({ date: newDate.toDate() });
    }, [format, onDateChange]);
    const handleInputChange = useCallback((e) => {
        setInputValue(e.target.value);
        const newDate = dayjs(lang, e.target.value, format);
        const isValidDate = isValidDateStr(e.target.value, format) &&
            !(disabledDate && disabledDate(newDate.toDate()));
        if (!isValidDate)
            return;
        setSelectedDate(newDate);
        if (onDateChange)
            onDateChange({ date: newDate.toDate() });
    }, [disabledDate, format, lang, onDateChange]);
    const handleClearBtnClick = useCallback(() => {
        setSelectedDate(undefined);
        setInputValue('');
        setOpen(true);
        // @ts-ignore
        if (inputRef && inputRef.current)
            inputRef.current.focus();
        if (onDateChange)
            onDateChange({ date: undefined });
    }, [inputRef, onDateChange]);
    const handleInputClick = useCallback(() => setOpen(true), []);
    useEffect(() => {
        // update new valid date when input blur
        if (typeof inputRef === 'function')
            return undefined;
        if (!inputRef || !inputRef.current)
            return undefined;
        const inputElement = inputRef.current;
        const handleBlur = () => {
            // check date is a invalid format or disabled date
            const isValidDate = isValidDateStr(inputValue, format) &&
                !(disabledDate && disabledDate(dayjs(lang, inputValue, format).toDate()));
            if (!isValidDate) {
                // fallback to previous valid date
                setSelectedDate(date);
                setInputValue(date ? date.format(format) : '');
            }
            setOpen(false); // close calendar when blur
        };
        inputElement.addEventListener('blur', handleBlur);
        return () => {
            inputElement.removeEventListener('blur', handleBlur);
        };
    }, [disabledDate, format, inputRef, inputValue, lang, date]);
    useEffect(() => {
        setInputValue(value ? dayjs(lang, value).format(format) : inputValue);
    }, [lang, format, value]); // eslint-disable-line react-hooks/exhaustive-deps
    return (React.createElement(Container, { block: block, ref: containerRef },
        title && (React.createElement(Title, { ref: titleRef, onClick: handleClearBtnClick }, title)),
        React.createElement(DateInput, Object.assign({ lang: lang, ref: inputRef, value: inputValue, width: inputWidth, block: block, status: status, disabled: disabled, placeholder: placeholder || getDefaultPlaceholder(lang), onChange: handleInputChange, onClick: handleInputClick, onCalendarIconClick: handleInputClick, onClearBtnClick: handleClearBtnClick, tooltipDirection: calendarPosition === 'TOP' ? 'BOTTOM_CENTER' : 'TOP_CENTER', tooltipText: getDefaultInputTooltip(lang).replace('{{format}}', format), clearBtnTooltipText: getDefaultClearTooltip(lang) }, restProps)),
        React.createElement(MessageWrapper, null, renderMessages.map(renderMessage => (React.createElement(Message, { status: status, withBullet: renderMessages.length > 1, key: shortid.generate() }, renderMessage())))),
        React.createElement(CalendarContainer, { top: title ? 4.25 : 2.75, bottom: getCalendarBottomPosition({
                containerRef,
                titleRef,
            }), open: !disabled && open, onMouseDown: e => e.preventDefault(), calendarAlign: calendarAlign, calendarPosition: calendarPosition },
            React.createElement(Calendar, { lang: lang, open: !disabled && open, selectedDate: date, onChange: handleDateChange, disabledDate: disabledDate }))));
};
export default React.memo(React.forwardRef((props, ref) => {
    if (typeof ref === 'function')
        throw new Error('ref callback is not supported');
    return React.createElement(DatePicker, Object.assign({ inputRef: ref || React.createRef() }, props));
}));
//# sourceMappingURL=DatePicker.js.map